import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthUser, Organization, OrgRole } from '@nargus/shared';
import { LoaderCircle, LogOut, Plus, QrCode, Trash2 } from 'lucide-react';
import {
  useCreateInvite,
  useInvites,
  useMembers,
  useRemoveMember,
  useRevokeInvite,
} from '@/resources/organization/organization.service';
import { InviteQr } from '@/invite/invite-qr';
import { UserAvatar } from '@/components/user-avatar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  EmptyNote,
  ErrorNote,
  RoleBadge,
  SectionHeader,
  formatDate,
  useRoleLabels,
} from './ui';

export function MembersSection({
  organization,
  user,
}: {
  organization: Organization;
  user: AuthUser;
}) {
  const { t } = useTranslation();
  const isOwner = organization.role === 'owner';
  const hint =
    organization.role === 'owner'
      ? 'members.ownerHint'
      : organization.role === 'friend'
        ? 'members.friendHint'
        : 'members.memberHint';

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader title={organization.name} hint={t(hint)} />
      <MemberList organization={organization} user={user} isOwner={isOwner} />
      <InviteSection organization={organization} isOwner={isOwner} />
    </div>
  );
}

function MemberList({
  organization,
  user,
  isOwner,
}: {
  organization: Organization;
  user: AuthUser;
  isOwner: boolean;
}) {
  const { t } = useTranslation();
  const members = useMembers(organization.id);
  const remove = useRemoveMember();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('members.cardTitle')}</CardTitle>
        <CardDescription>
          {t('members.count', { count: organization.memberCount })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-1">
        {members.isPending ? (
          <>
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </>
        ) : (
          members.data?.map((member) => {
            const isSelf = member.userId === user.id;
            const canRemove = isSelf || isOwner;
            return (
              <div
                key={member.userId}
                className="flex items-center gap-3 rounded-lg py-2 hover:bg-muted/50"
              >
                <UserAvatar
                  userId={member.userId}
                  name={member.name}
                  email={member.email}
                  avatarUpdatedAt={member.avatarUpdatedAt}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {member.name ?? member.email}
                    {isSelf && (
                      <span className="text-muted-foreground"> {t('members.you')}</span>
                    )}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                </div>
                <RoleBadge role={member.role} />
                {canRemove && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={
                      isSelf
                        ? t('members.leave')
                        : t('members.remove', { email: member.email })
                    }
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate({
                        organizationId: organization.id,
                        userId: member.userId,
                      })
                    }
                  >
                    {isSelf ? <LogOut /> : <Trash2 />}
                  </Button>
                )}
              </div>
            );
          })
        )}
        <ErrorNote error={members.error ?? remove.error} />
      </CardContent>
    </Card>
  );
}

function InviteSection({
  organization,
  isOwner,
}: {
  organization: Organization;
  isOwner: boolean;
}) {
  const { t } = useTranslation();
  const roles = useRoleLabels();
  const [role, setRole] = useState<OrgRole>('member');
  const invites = useInvites(organization.id);
  const create = useCreateInvite();
  const revoke = useRevokeInvite();

  if (invites.isPending) return <Skeleton className="h-40 w-full max-w-md" />;

  return (
    <Card className="max-w-md">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="size-4" />
          {t('invite.cardTitle')}
        </CardTitle>
        <CardDescription>{t('invite.cardHint')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {invites.data?.length === 0 ? (
          <EmptyNote>{t('invite.none')}</EmptyNote>
        ) : (
          invites.data?.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-col gap-3 border-t pt-5 first:border-0 first:pt-0"
            >
              <div className="flex items-center gap-2">
                <RoleBadge role={invite.role} />
                <span className="flex-1 text-xs text-muted-foreground">
                  {t('invite.validUntil', { date: formatDate(invite.expiresAt) })}
                </span>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('invite.revoke')}
                    disabled={revoke.isPending}
                    onClick={() =>
                      revoke.mutate({
                        organizationId: organization.id,
                        inviteId: invite.id,
                      })
                    }
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
              <InviteQr token={invite.token} />
            </div>
          ))
        )}

        {isOwner && (
          <div className="flex flex-col gap-2 border-t pt-5">
            <Select
              items={roles}
              value={role}
              onValueChange={(value) => setRole(value as OrgRole)}
            >
              <SelectTrigger className="w-full" aria-label={t('invite.roleSelect')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">{roles.member}</SelectItem>
                <SelectItem value="friend">{roles.friend}</SelectItem>
                <SelectItem value="owner">{roles.owner}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              disabled={create.isPending}
              onClick={() => create.mutate({ organizationId: organization.id, role })}
            >
              {create.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Plus />
              )}
              {create.isPending ? t('invite.creating') : t('invite.create')}
            </Button>
          </div>
        )}

        <ErrorNote error={invites.error ?? create.error ?? revoke.error} />
      </CardContent>
    </Card>
  );
}
